import { FindingSpecDto, JamfSmartGroupDto } from '@ampsec/platform-client';
import { FaRegEdit } from '@react-icons/all-files/fa/FaRegEdit';
import { Typography } from 'Components/atoms';
import { Form } from 'react-bootstrap';
import { JAMF_SMART_GROUP_NAME, STATUS } from 'Core-utils/constants/constants';
import { opacitySmartGroupClassName, Status } from 'Core-utils/helpers/helpers';
import { TableColumn } from 'react-data-table-component';

export const SMART_GROUPS_CONFIG_COLUMNS = (
    handleEdit: (row: JamfSmartGroupDto) => void,
    handleStatusChange: (row: JamfSmartGroupDto, isChecked: boolean) => void,
    findingSpecsMap: Record<string, FindingSpecDto>,
    loadingRows: Set<string>,
    pendingStatusUpdates: Record<string, string>,
): TableColumn<JamfSmartGroupDto>[] => [
    {
        id: 'groupName',
        name: <Typography variant="body2">{JAMF_SMART_GROUP_NAME}</Typography>,
        selector: (row: JamfSmartGroupDto) => row.groupName,
        sortable: true,
        cell: (row: JamfSmartGroupDto) => {
            const specName = `JAMF_SMART_GROUP_${row.groupType}_${row.groupId}`;
            const findingSpec = findingSpecsMap?.[specName];
            return (
                <div className={`findings-config__name ${opacitySmartGroupClassName(row)}`}>
                    <Typography variant="body5">{row.groupName}</Typography>
                    {findingSpec?.displayValue && (
                        <Typography variant="caption1" color="text-medium-emphasis">
                            {findingSpec.displayValue}
                        </Typography>
                    )}
                </div>
            );
        },
        width: '35%',
    },
    {
        id: 'status',
        name: <Typography variant="body2">{STATUS}</Typography>,
        selector: (row: JamfSmartGroupDto) => pendingStatusUpdates[`${row.groupType}_${row.groupId}`] || row.status,
        sortable: true,
        cell: (row: JamfSmartGroupDto) => {
            const rowKey = `${row.groupType}_${row.groupId}`;
            const isLoading = loadingRows?.has(rowKey);
            const currentStatus = pendingStatusUpdates[rowKey] || row.status;

            const isEnabled = currentStatus === Status.ENABLED;
            const isArchived = row.status === Status.ARCHIVED;
            const statusLabel = isArchived ? 'ARCHIVED' : isEnabled ? 'ENABLED' : 'DISABLED';

            return (
                <div className={`smart-group__status-toggle-container ${opacitySmartGroupClassName(row)}`}>
                    <Form.Check
                        type="switch"
                        checked={isEnabled}
                        onChange={(e) => handleStatusChange(row, e.target.checked)}
                        disabled={isArchived || isLoading}
                        className="m-0 p-0"
                    />
                    <Typography
                        variant="caption1"
                        className="smart-group__status-label"
                        style={{ color: isEnabled ? '#2ecc71' : '#7f8c8d', fontWeight: 700 }}
                    >
                        {isLoading ? 'Loading...' : statusLabel}
                    </Typography>
                </div>
            );
        },
        sortFunction: (rowA: JamfSmartGroupDto, rowB: JamfSmartGroupDto) => {
            const sA = pendingStatusUpdates[`${rowA.groupType}_${rowA.groupId}`] || rowA.status;
            const sB = pendingStatusUpdates[`${rowB.groupType}_${rowB.groupId}`] || rowB.status;
            const weight = { [Status.ENABLED]: 3, [Status.DISABLED]: 2, [Status.ARCHIVED]: 1 };
            return (weight[sA as keyof typeof weight] || 0) - (weight[sB as keyof typeof weight] || 0);
        },
        width: '15rem',
    },
    {
        id: 'edit',
        cell: (row: JamfSmartGroupDto) => (
            <div className={`smart-group__edit-cell ${opacitySmartGroupClassName(row)}`}>
                <FaRegEdit className="smart-group__edit-icon" onClick={() => handleEdit(row)} size={18} />
            </div>
        ),
        width: '20rem',
        center: true,
    },
];
